import { MCP_CONSENT_PATH } from '@dawn/feature-config';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { NoAccess } from '@/components/NoAccess';
import { useConfigQuery } from '@/hooks/queries/use-config-query';
import { usePermissions } from '@/hooks/use-permissions';
import { AuthLayout } from '@/layouts/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { AccessDiscoveryPage } from '@/pages/AccessDiscoveryPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { InvitePage } from '@/pages/InvitePage';
import { LoginPage } from '@/pages/LoginPage';
import { McpAuthorizePage } from '@/pages/McpAuthorizePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { UsersPage } from '@/pages/UsersPage';

import { ProtectedRoute } from './ProtectedRoute';

export function AppRoutes() {
  // Same flag the sidebar reads, so an enabled Overview item has somewhere to
  // go. Without this the nav item would bounce straight back here.
  // Wait for it before deciding `/`: deciding on the not-yet-loaded config
  // redirects every fresh load away from an enabled Overview.
  const { data: config, isPending: configPending } = useConfigQuery();
  const overviewEnabled = config?.policy.featureFlags.overview === true;
  const overviewV2 = config?.policy.featureFlags.overviewV2 === true;
  const { can } = usePermissions();
  // Gated on the route element, never inside the page: the page mounts only with
  // data.view, so it makes no data request and its hooks never see a flip (TDEV-1213).
  const dataPage = (page: ReactElement) =>
    can('data.view') ? page : <NoAccess permission="data.view" />;

  let overviewElement = dataPage(<DashboardPage />);
  if (!overviewEnabled) overviewElement = <Navigate to="/access-discovery" replace />;
  else if (overviewV2) overviewElement = dataPage(<PlaceholderPage title="Overview v2" />);

  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite" element={<InvitePage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        {/* MCP OAuth consent (TDEV-1199): a full-page card, no dashboard chrome. */}
        <Route path={MCP_CONSENT_PATH} element={<McpAuthorizePage />} />
        <Route element={<DashboardLayout />}>
          {/* Overview is the landing route (on by default, TDEV-1219); an
              environment that turns it off in Firestore lands on Access
              Discovery instead. The flag gates the route as well as the nav
              item: an enabled item pointing at a redirect would look like the
              page is broken. */}
          <Route path="/" element={configPending ? null : overviewElement} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/access-discovery" element={dataPage(<AccessDiscoveryPage />)} />
          {/* Discovery Prompt is disabled (TDEV-949, WIP) — like Policies /
              Risky Identities it has no reachable route, so a direct visit
              404s rather than rendering with a mismatched header. Restore this
              route alongside re-enabling the nav item. */}
          {/* Bare /settings redirects to the default section inside SettingsPage;
              the :section variant carries the active settings tab in the URL. */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/:section" element={<SettingsPage />} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
